/**
 * SEC EDGAR API Service
 *
 * Client-side service for accessing SEC EDGAR data including:
 * - Company filings (10-K, 10-Q, 8-K, etc.)
 * - XBRL financial facts
 * - REIT portfolio analysis
 * - Cross-company comparisons
 */

import { apiClient } from './client'

// ============================================================================
// Types & Interfaces
// ============================================================================

export interface SECCompanyInfo {
  ticker: string
  cik: string
  formatted_cik: string
}

export interface SECFiling {
  accession_number: string
  filing_date: string
  report_date?: string
  form_type: string
  file_number?: string
  film_number?: string
  items?: string
  primary_document?: string
  primary_doc_description?: string
}

export interface SECFinancialMetric {
  concept: string
  latest_value: number
  latest_date: string
  unit: string
  historical_count: number
}

export interface SECREITInfo {
  cik: string
  ticker: string
  name: string
}

export interface SECSubmissionsResponse {
  success: boolean
  cik: string
  data: {
    cik: string
    name: string
    entityType: string
    sic?: string
    sicDescription?: string
    filings: {
      recent: {
        accessionNumber: string[]
        filingDate: string[]
        reportDate: string[]
        form: string[]
        primaryDocument: string[]
      }
    }
  }
  timestamp: string
}

export interface SECCompanyFactsResponse {
  success: boolean
  cik: string
  data: {
    cik: string
    entityName: string
    facts: {
      'us-gaap': Record<string, any>
      dei?: Record<string, any>
    }
  }
  timestamp: string
}

export interface SECCompanyConceptResponse {
  success: boolean
  cik: string
  taxonomy: string
  concept: string
  data: any
  timestamp: string
}

export interface SECFramesResponse {
  success: boolean
  taxonomy: string
  concept: string
  unit: string
  period: string
  data: any
  timestamp: string
}

export interface SECFilingsResponse {
  success: boolean
  cik: string
  form_type?: string
  count: number
  filings: SECFiling[]
  timestamp: string
}

export interface SECREITPortfolioResponse {
  success: boolean
  data: {
    cik: string
    financial_metrics: Record<string, SECFinancialMetric>
    recent_filings: SECFiling[]
    extracted_at: string
  }
  timestamp: string
}

export interface SECREITListResponse {
  success: boolean
  count: number
  reits: SECREITInfo[]
  timestamp: string
}

export interface SECCompareREITsResponse {
  success: boolean
  data: {
    concept: string
    comparison: Record<string, {
      value: number
      date: string
      unit: string
    }>
    timestamp: string
  }
  timestamp: string
}

export interface SECRealEstateConceptsResponse {
  success: boolean
  concepts: Record<string, {
    description: string
    category: string
    typical_use: string
  }>
  count: number
  timestamp: string
}

export interface SECExamplesResponse {
  success: boolean
  examples: Record<string, {
    endpoint: string
    example: any
    description: string
  }>
  timestamp: string
}

// ============================================================================
// SEC EDGAR Service Class
// ============================================================================

export class SECEdgarService {
  private static readonly BASE_PATH = '/sec-edgar'

  /**
   * Search for a company by ticker symbol
   */
  static async searchByTicker(ticker: string): Promise<SECCompanyInfo> {
    const response = await apiClient.get(
      `${this.BASE_PATH}/company/search/${ticker.toUpperCase()}`
    ) as unknown as SECCompanyInfo
    if (!response) {
      throw new Error('No data returned from API')
    }
    return response
  }

  /**
   * Get full filing history for a company
   */
  static async getCompanySubmissions(cik: string): Promise<SECSubmissionsResponse> {
    const response = await apiClient.post(
      `${this.BASE_PATH}/company/submissions`,
      { cik }
    ) as unknown as SECSubmissionsResponse
    if (!response) throw new Error('No data returned from API')
    return response
  }

  /**
   * Get all XBRL financial facts for a company
   */
  static async getCompanyFacts(cik: string): Promise<SECCompanyFactsResponse> {
    const response = await apiClient.post(
      `${this.BASE_PATH}/company/facts`,
      { cik }
    ) as unknown as SECCompanyFactsResponse
    if (!response) throw new Error('No data returned from API')
    return response
  }

  /**
   * Get single financial concept across all years for a company
   */
  static async getCompanyConcept(
    cik: string,
    concept: string,
    taxonomy: string = 'us-gaap'
  ): Promise<SECCompanyConceptResponse> {
    const response = await apiClient.post(
      `${this.BASE_PATH}/company/concept`,
      { cik, taxonomy, concept }
    ) as unknown as SECCompanyConceptResponse
    if (!response) throw new Error('No data returned from API')
    return response
  }

  /**
   * Get aggregated facts for a concept across all companies in a period
   */
  static async getFrames(
    taxonomy: string,
    concept: string,
    unit: string,
    period: string
  ): Promise<SECFramesResponse> {
    const response = await apiClient.post(
      `${this.BASE_PATH}/frames`,
      { taxonomy, concept, unit, period }
    ) as unknown as SECFramesResponse
    if (!response) throw new Error('No data returned from API')
    return response
  }

  /**
   * Get recent filings for a company
   */
  static async getRecentFilings(
    cik: string,
    formType?: string,
    limit: number = 10
  ): Promise<SECFilingsResponse> {
    const params = new URLSearchParams({
      limit: limit.toString()
    })

    if (formType) {
      params.append('form_type', formType)
    }

    const response = await apiClient.get(
      `${this.BASE_PATH}/company/filings/${cik}?${params.toString()}`
    ) as unknown as SECFilingsResponse
    if (!response) throw new Error('No data returned from API')
    return response
  }

  /**
   * Get REIT portfolio data
   */
  static async getREITPortfolio(cik: string): Promise<SECREITPortfolioResponse> {
    const response = await apiClient.post(
      `${this.BASE_PATH}/reit/portfolio`,
      { cik }
    ) as unknown as SECREITPortfolioResponse
    if (!response) throw new Error('No data returned from API')
    return response
  }

  /**
   * Get list of major REITs
   */
  static async getREITList(limit: number = 50): Promise<SECREITListResponse> {
    const response = await apiClient.get(
      `${this.BASE_PATH}/reit/list?limit=${limit}`
    ) as unknown as SECREITListResponse
    if (!response) throw new Error('No data returned from API')
    return response
  }

  /**
   * Compare specific metric across multiple REITs
   */
  static async compareREITs(
    ciks: string[],
    concept: string = 'RealEstateInvestmentPropertyNet'
  ): Promise<SECCompareREITsResponse> {
    const response = await apiClient.post(
      `${this.BASE_PATH}/reit/compare`,
      { ciks, concept }
    ) as unknown as SECCompareREITsResponse
    if (!response) throw new Error('No data returned from API')
    return response
  }

  /**
   * Get list of real estate XBRL concepts
   */
  static async getRealEstateConcepts(): Promise<SECRealEstateConceptsResponse> {
    const response = await apiClient.get(
      `${this.BASE_PATH}/concepts/real-estate`
    ) as unknown as SECRealEstateConceptsResponse
    if (!response) throw new Error('No data returned from API')
    return response
  }

  /**
   * Get example API queries
   */
  static async getExamples(): Promise<SECExamplesResponse> {
    const response = await apiClient.get(
      `${this.BASE_PATH}/examples`
    ) as unknown as SECExamplesResponse
    if (!response) throw new Error('No data returned from API')
    return response
  }

  /**
   * Get health check status
   */
  static async healthCheck(): Promise<{ status: string; service: string; timestamp: string }> {
    const response = await apiClient.get(
      `${this.BASE_PATH}/health`
    ) as unknown as { status: string; service: string; timestamp: string }
    if (!response) throw new Error('No data returned from API')
    return response
  }
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Format CIK to display format
 */
export function formatCIK(cik: string): string {
  return `CIK${cik.padStart(10, '0')}`
}

/**
 * Format currency value
 */
export function formatCurrency(value: number): string {
  if (value >= 1_000_000_000) {
    return `$${(value / 1_000_000_000).toFixed(2)}B`
  } else if (value >= 1_000_000) {
    return `$${(value / 1_000_000).toFixed(2)}M`
  } else if (value >= 1_000) {
    return `$${(value / 1_000).toFixed(2)}K`
  }
  return `$${value.toFixed(2)}`
}

/**
 * Get filing type badge color
 */
export function getFilingTypeBadge(formType: string): {
  variant: 'default' | 'secondary' | 'outline' | 'destructive'
  label: string
} {
  switch (formType) {
    case '10-K':
      return { variant: 'default', label: 'Annual Report' }
    case '10-Q':
      return { variant: 'secondary', label: 'Quarterly Report' }
    case '8-K':
      return { variant: 'outline', label: 'Current Report' }
    case 'S-11':
      return { variant: 'default', label: 'REIT Registration' }
    case 'DEF 14A':
      return { variant: 'secondary', label: 'Proxy Statement' }
    case '424B':
      return { variant: 'outline', label: 'Prospectus' }
    default:
      return { variant: 'outline', label: formType }
  }
}

/**
 * Common XBRL concepts for real estate
 */
export const COMMON_REAL_ESTATE_CONCEPTS = {
  assets: {
    RealEstateInvestmentPropertyNet: 'Net Real Estate Assets',
    RealEstateInvestmentPropertyGross: 'Gross Real Estate Assets',
    RealEstateInvestmentPropertyAccumulatedDepreciation: 'Accumulated Depreciation',
    PropertyPlantAndEquipmentNet: 'Net PP&E',
    Assets: 'Total Assets'
  },
  liabilities: {
    LongTermDebt: 'Long-term Debt',
    MortgageDebt: 'Mortgage Debt',
    Liabilities: 'Total Liabilities'
  },
  income: {
    RevenueFromContractWithCustomerIncludingAssessedTax: 'Total Revenue',
    NetIncomeLoss: 'Net Income',
    OperatingIncomeLoss: 'Operating Income',
    InterestExpense: 'Interest Expense'
  },
  equity: {
    StockholdersEquity: 'Shareholders Equity',
    CommonStockSharesOutstanding: 'Shares Outstanding'
  }
} as const

/**
 * Filing type options for filters
 */
export const FILING_TYPES = [
  { value: '10-K', label: '10-K (Annual Report)' },
  { value: '10-Q', label: '10-Q (Quarterly Report)' },
  { value: '8-K', label: '8-K (Current Report)' },
  { value: 'S-11', label: 'S-11 (REIT Registration)' },
  { value: 'DEF 14A', label: 'DEF 14A (Proxy Statement)' },
  { value: '424B', label: '424B (Prospectus)' }
] as const

export default SECEdgarService
